import * as React from 'react';
import { cn } from '../../lib/utils';

export interface LiveTagProps {
  /** Tag id / display label (e.g. `Line3.Temperature`). */
  tagId: string;
  /** Current value. */
  value: number | string | null | undefined;
  /** Optional unit. */
  unit?: string;
  /** Last-update timestamp — drives the freshness indicator. */
  updatedAt?: number | string | Date;
  /** Pre-computed freshness label (e.g. "12s"). If omitted, none rendered. */
  freshnessLabel?: string;
  /** Connection quality. Drives the dot color + pulse behavior. */
  quality?: 'good' | 'uncertain' | 'bad' | 'stale';
  /** Compact mode hides label, shows just dot + value. */
  compact?: boolean;
  className?: string;
}

const QUALITY_COLOR: Record<NonNullable<LiveTagProps['quality']>, string> = {
  good: 'hsl(var(--accent-live))',
  uncertain: 'hsl(var(--alarm-minor))',
  bad: 'hsl(var(--alarm-critical))',
  stale: 'hsl(var(--muted-foreground))',
};

/**
 * Tyndo LiveTag — a single live data point with quality + freshness.
 *
 * Renders: pulse dot · tagId · value · unit · "12s ago"
 * The default Phosphor color signals "live"; the dot pulses while quality is
 * 'good'. When stale or bad the pulse stops and the dot dims.
 */
export function LiveTag({
  tagId,
  value,
  unit,
  freshnessLabel,
  quality = 'good',
  compact,
  className,
}: LiveTagProps) {
  const color = QUALITY_COLOR[quality];
  const pulsing = quality === 'good';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-md border border-border bg-surface-raised px-2 py-1',
        compact ? 'gap-1 px-1.5 py-0.5' : '',
        className
      )}
    >
      <span
        aria-hidden
        className="relative flex h-2 w-2 items-center justify-center"
      >
        {pulsing && (
          <span
            className="absolute inline-flex h-full w-full rounded-full opacity-50 tyndo-pulse"
            style={{ backgroundColor: color }}
          />
        )}
        <span
          className="relative inline-flex h-2 w-2 rounded-full"
          style={{ backgroundColor: color }}
        />
      </span>
      {!compact && (
        <span className="t-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          {tagId}
        </span>
      )}
      <span className="t-tabular tabular-nums text-sm font-semibold text-foreground">
        {value === null || value === undefined ? '—' : value}
        {unit && <span className="ml-0.5 text-[10px] text-muted-foreground font-normal">{unit}</span>}
      </span>
      {freshnessLabel && !compact && (
        <span className="t-mono text-[10px] text-muted-foreground">
          {freshnessLabel}
        </span>
      )}
    </span>
  );
}
